import { useMutation } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useNavigate } from "react-router-dom";

import { ROUTES } from "@/constants/routes";
import { instance } from "@/lib/axios/instance";
import { ResetPasswordRequest } from "@/types/request/ResetPasswordRequest";
import { ResetPasswordErrorResponse } from "@/types/response/ResetPasswordResponse";

/**
 * Hook to handle reset password mutation.
 * Navigates to login page with success state after successful password reset.
 *
 * @returns Mutation object for reset password operation
 *
 * @example
 * ```tsx
 * const resetPasswordMutation = useResetPasswordMutation();
 * resetPasswordMutation.mutate({
 *   email: "user@example.com",
 *   token: "reset-token",
 *   password: "newPassword",
 *   confirmPassword: "newPassword"
 * });
 * ```
 */
export function useResetPasswordMutation() {
  const navigate = useNavigate();

  return useMutation<
    AxiosResponse,
    AxiosError<ResetPasswordErrorResponse>,
    ResetPasswordRequest
  >({
    mutationFn: (resetPasswordRequest: ResetPasswordRequest) =>
      instance.post("/api/v2/Auth/reset-password", resetPasswordRequest),
    onSuccess: () => {
      navigate(ROUTES.login, { state: { passwordReset: true } });
    },
  });
}
